import { getOperationName } from '@apollo/client/utilities';
import { UUID } from '@cmg/common';
import { SnackbarManager } from '@cmg/design-system';
import React from 'react';

import { IndicationStatus } from '../../../../../../../../../../graphql';
import {
  Calendar_PublishedOfferingsBuySideDocument,
  Calendar_PublishedOfferingsDocument,
} from '../../../../../../../../../calendar/components/calendar-grid/graphql/__generated__';
import { updateDemandGridRowsReactiveVar } from '../../../../../../../../../order-book/institutional-demand/hooks/useUpdateDemandGridRowsReactiveVar';
import {
  parseFirmKey,
  useCmgEntityKeyContext,
} from '../../../../../../../../contexts/CmgEntityKeyContext';
import {
  useManageIndication_PassOnInstitutionalIndicationMutation,
  useManageIndication_PassOnOfferingWithoutInstitutionalIndicationMutation,
} from '../../../../graphql/__generated__';

export const refetchQueries = [
  getOperationName(Calendar_PublishedOfferingsDocument)!,
  getOperationName(Calendar_PublishedOfferingsBuySideDocument)!,
];

export type usePassOnInstitutionalIndicationParams = {
  offeringId: UUID;
  onClose: () => void;
  indicationId?: UUID;
  indicationStatus?: IndicationStatus | null;
  isPassingNewIndication?: boolean;
  onFinishEditing?: () => void;
};

export const usePassOnInstitutionalIndication = ({
  offeringId,
  onClose,
  indicationId = undefined,
  indicationStatus = null,
  isPassingNewIndication = false,
  onFinishEditing,
}: usePassOnInstitutionalIndicationParams): [() => Promise<void>, boolean] => {
  const [passIndication, { loading: isPassingIndication }] =
    useManageIndication_PassOnInstitutionalIndicationMutation();
  const { cmgEntityKey, updateCmgEntityKey } = useCmgEntityKeyContext();
  const [passWithoutIndication, { loading: isPassingWithoutIndication }] =
    useManageIndication_PassOnOfferingWithoutInstitutionalIndicationMutation();
  const firmKeyOnUrl = parseFirmKey(cmgEntityKey);

  const handlePassWithoutIndication = React.useCallback(async () => {
    const response = await passWithoutIndication({
      variables: {
        offeringId,
        input: {
          investorInformation: firmKeyOnUrl ? { bankInvestorKey: firmKeyOnUrl } : { cmgEntityKey },
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    });

    const responseIndication = response.data!.passOnOfferingWithoutInstitutionalIndication;

    if (responseIndication.__typename === 'ServiceError') {
      throw new Error('Passsing new indication failed');
    }

    if (firmKeyOnUrl) {
      updateCmgEntityKey(responseIndication.investorInformation.cmgEntityKey!);
    }

    return responseIndication.id;
  }, [cmgEntityKey, firmKeyOnUrl, offeringId, passWithoutIndication, updateCmgEntityKey]);

  const handlePassIndication = React.useCallback(async () => {
    await passIndication({
      variables: {
        offeringId,
        indicationId: indicationId!,
        activateIfCancelled: indicationStatus === IndicationStatus.Cancelled,
      },
      awaitRefetchQueries: true,
      refetchQueries,
    });

    return indicationId!;
  }, [indicationId, indicationStatus, offeringId, passIndication]);

  const handlePass = React.useCallback(async () => {
    try {
      const passedIndicationId = isPassingNewIndication
        ? await handlePassWithoutIndication()
        : await handlePassIndication();

      SnackbarManager.success('Successfully passed indication');
      updateDemandGridRowsReactiveVar({ fetchIds: [passedIndicationId] });
      onClose();
      onFinishEditing?.();
    } catch {
      SnackbarManager.error('Failed to pass indication');
    }
  }, [
    handlePassIndication,
    handlePassWithoutIndication,
    isPassingNewIndication,
    onClose,
    onFinishEditing,
  ]);

  return [handlePass, isPassingIndication || isPassingWithoutIndication];
};
